import { ChevronRight } from "lucide-react";

import { ActivityQuote, ActivityTimestamp, UserName } from "./ActivityEventRow";

import { Column, RichTextDisplay, Row, Text } from "@/components";
import { Avatar } from "@/components/quick-access/Avatar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

import { useGetUser } from "@/hooks/queries/useGetUser";
import { activityTypography } from "@/lib/constants";
import { type CandidacySummaryChange } from "@/types";

// Custom layout: the collapsible "View more" wraps the whole body column.
export const SummaryChangeEvent = ({
  candidateFullName,
  currentUserId,
  summaryChange,
}: {
  candidateFullName: string;
  currentUserId: number;
  summaryChange: CandidacySummaryChange;
}) => {
  /* Database CRUD Operations */
  // Read
  const { data: user } = useGetUser({ userId: summaryChange.userId });

  /* Render */
  const render = () => {
    return (
      <Row className="gap-2">
        <Avatar
          initials={user ? `${user.firstName[0]}${user.lastName[0]}` : "?"}
        />
        <Collapsible className="group/collapsible">
          <Column className="items-start gap-0.5">
            <Text className={activityTypography.body} variant="secondary">
              <UserName currentUserId={currentUserId} user={user} /> updated the
              executive summary for{" "}
              <span className={activityTypography.emphasis}>
                {candidateFullName}
              </span>
            </Text>
            <CollapsibleTrigger asChild>
              <Row className="cursor-pointer items-center gap-1">
                <Text className="text-xs font-normal text-primary-900 group-data-[state=open]/collapsible:hidden">
                  View more
                </Text>
                <Text className="text-xs font-normal text-primary-900 group-data-[state=closed]/collapsible:hidden">
                  View less
                </Text>
                <ChevronRight className="h-3 w-3 text-primary-900 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
              </Row>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <ActivityQuote>
                <Text className={activityTypography.quote} variant="secondary">
                  <RichTextDisplay richText={summaryChange.toSummary} />
                </Text>
              </ActivityQuote>
            </CollapsibleContent>
            <ActivityTimestamp createdAt={summaryChange.createdAt} />
          </Column>
        </Collapsible>
      </Row>
    );
  };

  return render();
};
